import { useCallback, useState } from 'react'
import { useSession } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import type { AssistantMessageDisplay } from '@/types'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Message,
  MessageContent,
  MessageResponse,
} from '@/components/ai/message'
import {
  CHAT_MARKDOWN_COMPONENTS,
  CHAT_MARKDOWN_TABLE_ONLY,
} from '@/components/AgentChat/ChatMarkdownLink'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { createDocument, DEFAULT_DOCUMENT_TYPE } from '@/features/documents'
import { MessageFooter } from './MessageFooter'

interface AssistantMessageProps {
  agentId: 'david' | 'hanna'
  event: AssistantMessageDisplay
}

export function AssistantMessage({ agentId, event }: AssistantMessageProps) {
  const { toTask } = useAppNavigate()
  const { session } = useSession()
  const queryClient = useQueryClient()
  const { configuration } = useConfiguration()
  const saveResponseEnabled = configuration?.features?.save_response ?? false
  const [isSaving, setIsSaving] = useState(false)

  const handleCopy = useCallback(() => toast.success('Copied to clipboard'), [])
  const handleCopyError = useCallback(() => toast.error('Failed to copy'), [])

  // Create a document seeded with this response, then open its editor tab.
  // Name/type are set afterwards via the editor's "Edit details" modal.
  const handleSaveResponse = useCallback(async () => {
    setIsSaving(true)
    try {
      const token = await session?.getToken()
      if (!token) throw new Error('No authentication token available')
      const record = await createDocument(
        agentId,
        {
          name: 'Untitled document',
          description: '',
          access: [],
          metadata: { type: DEFAULT_DOCUMENT_TYPE },
          data: event.content,
        },
        token
      )
      await queryClient.invalidateQueries({ queryKey: ['documents'] })
      toTask(`/docs/${record.id}`)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save response'
      )
    } finally {
      setIsSaving(false)
    }
  }, [agentId, event.content, toTask, session, queryClient])

  return (
    <Message from="assistant">
      <MessageContent className="text-primary-high-contrast group-[.is-assistant]:text-primary-high-contrast group-[.is-assistant]:text-base leading-normal py-3">
        <MessageResponse
          animated
          isAnimating={event.isStreaming}
          mode={event.isStreaming ? 'streaming' : 'static'}
          // David only: intercept internal links for in-app navigation. Hanna
          // renders through the same component, so keep default link behavior
          // — but every agent gets the table toolbar fix.
          components={
            agentId === 'david'
              ? CHAT_MARKDOWN_COMPONENTS
              : CHAT_MARKDOWN_TABLE_ONLY
          }>
          {event.content}
        </MessageResponse>
        {!event.isStreaming && (
          <MessageFooter>
            <CopyButton
              content={event.content}
              onCopy={handleCopy}
              onError={handleCopyError}
            />
            {saveResponseEnabled && (
              <button
                type="button"
                title="Save response"
                aria-label="Save response"
                onClick={handleSaveResponse}
                disabled={isSaving}
                className="cursor-pointer disabled:opacity-50">
                <Save className="w-4 h-4" />
              </button>
            )}
          </MessageFooter>
        )}
      </MessageContent>
    </Message>
  )
}
